import React, { useState } from 'react';
import { Trash2, AlertTriangle, CheckCircle, Loader, FileImage, HardDrive } from 'lucide-react';
import { Button } from './ui';
import { useToast } from './ui/ToastContext';
import { 
  analyzeWasabiCleanup, 
  deleteOrphanedFiles, 
  isCleanupSafe 
} from '../utils/wasabiCleanup';

const WasabiCleanupTool = () => {
  const { toast } = useToast();
  const [step, setStep] = useState('analyze');
  const [loading, setLoading] = useState(false);
  const [analysisResults, setAnalysisResults] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState(new Set());
  const [deletionResults, setDeletionResults] = useState(null);
  const [error, setError] = useState(null);

  const runAnalysis = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const isSafe = await isCleanupSafe();
      if (!isSafe) {
        throw new Error('Cleanup safety check failed. Check Wasabi and database connections.');
      }
      
      const results = await analyzeWasabiCleanup();
      setAnalysisResults(results);
      setStep('review');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const runCleanup = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const filesToDelete = Array.from(selectedFiles);
      if (filesToDelete.length === 0) {
        toast.warning('No files selected for deletion');
        return;
      }
      
      const results = await deleteOrphanedFiles(filesToDelete);
      setDeletionResults(results);
      setStep('complete');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      <div className="text-center">
        <h1 className="text-3xl font-bold mb-2">Wasabi Storage Cleanup</h1>
        <p className="text-gray-600">
          Find and remove orphaned images that are no longer used by any props
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center gap-2 text-red-800">
            <AlertTriangle className="h-5 w-5" />
            <span className="font-medium">Error</span>
          </div>
          <p className="text-red-700 mt-2">{error}</p>
        </div>
      )}

      {step === 'analyze' && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <HardDrive className="h-5 w-5" />
            Step 1: Analyze Storage Usage
          </h2>
          <p className="text-gray-600 mb-4">
            Scan your Wasabi bucket and compare with active database records.
          </p>
          <Button 
            onClick={runAnalysis} 
            disabled={loading}
            variant="primary"
          >
            {loading ? 'Analyzing...' : 'Start Analysis'}
          </Button>
        </div>
      )}

      {step === 'review' && analysisResults && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-blue-50 p-4 rounded-lg text-center">
              <div className="text-2xl font-bold text-blue-600">
                {analysisResults.summary.totalFiles}
              </div>
              <div className="text-sm text-blue-800">Total Files</div>
            </div>
            <div className="bg-green-50 p-4 rounded-lg text-center">
              <div className="text-2xl font-bold text-green-600">
                {analysisResults.summary.activeFiles}
              </div>
              <div className="text-sm text-green-800">In Use</div>
            </div>
            <div className="bg-red-50 p-4 rounded-lg text-center">
              <div className="text-2xl font-bold text-red-600">
                {analysisResults.summary.orphanedFiles}
              </div>
              <div className="text-sm text-red-800">Orphaned</div>
            </div>
            <div className="bg-purple-50 p-4 rounded-lg text-center">
              <div className="text-2xl font-bold text-purple-600">
                {analysisResults.summary.potentialSavings}
              </div>
              <div className="text-sm text-purple-800">Savings</div>
            </div>
          </div>

          {analysisResults.orphanedFiles.length > 0 ? (
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-semibold mb-4">
                Orphaned Files ({analysisResults.orphanedFiles.length})
              </h3>
              
              <div className="max-h-96 overflow-y-auto space-y-2 mb-4">
                {analysisResults.orphanedFiles.map(file => (
                  <div 
                    key={file.key} 
                    className={`border rounded p-3 cursor-pointer ${
                      selectedFiles.has(file.key) ? 'bg-red-50 border-red-200' : 'hover:bg-gray-50'
                    }`}
                    onClick={() => {
                      const newSelection = new Set(selectedFiles);
                      if (newSelection.has(file.key)) {
                        newSelection.delete(file.key);
                      } else {
                        newSelection.add(file.key);
                      }
                      setSelectedFiles(newSelection);
                    }}
                  >
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={selectedFiles.has(file.key)}
                        readOnly
                      />
                      <div className="flex-1">
                        <div className="font-medium text-sm">{file.key.split('/').pop()}</div>
                        <div className="text-xs text-gray-500">
                          {file.sizeFormatted} • {file.ageInDays} days old
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {selectedFiles.size > 0 && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex justify-between items-center">
                    <div>
                      <div className="font-medium text-red-800">
                        {selectedFiles.size} files selected for deletion
                      </div>
                      <div className="text-sm text-red-600">
                        This action cannot be undone
                      </div>
                    </div>
                    <Button
                      onClick={runCleanup}
                      disabled={loading}
                      variant="danger"
                    >
                      {loading ? 'Deleting...' : 'Delete Selected'}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
              <CheckCircle className="h-12 w-12 text-green-600 mx-auto mb-3" />
              <h3 className="font-semibold text-green-800 mb-2">No Orphaned Files!</h3>
              <p className="text-green-700">Your storage is clean.</p>
            </div>
          )}
        </div>
      )}

      {step === 'complete' && deletionResults && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold mb-4">
            Cleanup Complete!
          </h2>
          
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="bg-green-50 p-4 rounded-lg text-center">
              <div className="text-2xl font-bold text-green-600">{deletionResults.totalDeleted}</div>
              <div className="text-sm text-green-800">Files Deleted</div>
            </div>
            <div className="bg-red-50 p-4 rounded-lg text-center">
              <div className="text-2xl font-bold text-red-600">{deletionResults.totalFailed}</div>
              <div className="text-sm text-red-800">Failed</div>
            </div>
          </div>

          <Button 
            onClick={() => {
              setStep('analyze');
              setAnalysisResults(null);
              setDeletionResults(null);
              setSelectedFiles(new Set());
            }} 
            variant="primary"
          >
            Run Another Analysis
          </Button>
        </div>
      )}
    </div>
  );
};

export default WasabiCleanupTool;
